import {
  AccountId,
  ContractId,
  ContractFunctionParameters, // --- This import is new ---
} from "@hashgraph/sdk";
import { Button, TextField, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { ContractFunctionParameterBuilder } from "../services/wallets/contractFunctionParameterBuilder";
import { useWalletInterface } from "../services/wallets/useWalletInterface";
import { useEffect, useState } from "react";

// --- Imports all from mirrorUtils ---
import {
  getTokenBalance,
  isAssociatedWithToken,
  getDepositedBalance,
  getTokenAllowance,
} from "../services/mirrorUtils";

// --- CONFIGURATION ---
// Set this date to toggle between Deposit and Withdraw modes
// e.g., new Date("2025-11-01T04:00:00Z") for Nov 1, 2025 UTC
const CUTOFF_DATE = new Date("2025-11-01T04:00:00Z"); // <-- ADJUST THIS DATE FOR TESTING

const usdcTokenId = "0.0.456858";
const tdrillTokenId = "0.0.9484420";
const contractId = "0.0.10061730";
const allowanceLearnMoreUrl = "https://tozm.io/?month10";

export default function Home() {
  const { walletInterface, accountId } = useWalletInterface();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false); // Used for withdraw success
  const [depositSuccess, setDepositSuccess] = useState(false); // Used for deposit success

  // --- State for both modes ---
  const [isDepositPhase, setIsDepositPhase] = useState(
    new Date() < CUTOFF_DATE,
  );

  // --- State for Withdraw Mode ---
  const [contractHasTdrill, setContractHasTdrill] = useState(false);
  const [userDepositedBalance, setUserDepositedBalance] = useState<bigint>(
    BigInt(0),
  );
  const [isTdrillAssociated, setIsTdrillAssociated] = useState(false);

  // --- State for Deposit Mode ---
  const [isUsdcAssociated, setIsUsdcAssociated] = useState(false);
  const [userTdrillBalance, setUserTdrillBalance] = useState(0);
  const [allowance, setAllowance] = useState(0);
  const [depositAmount, setDepositAmount] = useState("");


/*
  // --- MINIMAL SOLUTION ---
  // This return statement overrides the entire dApp functionality
  // with the "drill ended" message.
  return (
    <Stack alignItems="center" spacing={2} p={4} maxWidth={600} mx="auto">
      <Typography variant="h5" color="white" align="center">
        The October drill has ended. Be sure you are associated with Ttrader by October 31st. Visit tozm.io for more info.
      </Typography>
    </Stack>
  );
  // --- END OF MINIMAL SOLUTION ---
*/



  const checkStatus = async () => {
    if (!accountId) return;

    setLoading(true);
    // Refresh which phase we are in
    const inDepositPhase = new Date() < CUTOFF_DATE;
    setIsDepositPhase(inDepositPhase);

    try {
      if (inDepositPhase) {
        // --- DEPOSIT PHASE CHECKS ---
        const userAccountId = accountId.toString();

        // 1. Check for USDC association
        const usdcAssociated = await isAssociatedWithToken(
          userAccountId,
          usdcTokenId,
        );
        setIsUsdcAssociated(usdcAssociated);

        // 2. Check user's Tdrill wallet balance
        const tdrillBalance = await getTokenBalance(userAccountId, tdrillTokenId);
        setUserTdrillBalance(tdrillBalance);

        // 3. Check Tdrill allowance given to the contract
        const tdrillAllowance = await getTokenAllowance(
          userAccountId,
          contractId,
          tdrillTokenId,
        );
        setAllowance(tdrillAllowance);
      } else {
        // --- WITHDRAW PHASE CHECKS ---
        // 1. Check if user is associated with Tdrill
        const tdrillAssociated = await isAssociatedWithToken(
          accountId.toString(),
          tdrillTokenId,
        );
        setIsTdrillAssociated(tdrillAssociated);

        // 2. Check user's deposited balance *in* the contract
        const deposited = await getDepositedBalance(
          contractId,
          AccountId.fromString(accountId),
        );
        setUserDepositedBalance(deposited);

        // 3. Check the contract's total Tdrill balance
        const contractTdrill = await getTokenBalance(contractId, tdrillTokenId);
        setContractHasTdrill(contractTdrill > 0);
      }
    } catch (err) {
      console.error("Error checking status:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, [accountId]);

  const handleWithdraw = async () => {
    if (!walletInterface) return;

    setLoading(true);
    const params = new ContractFunctionParameterBuilder(); // No parameters
    const gasLimit = 100_000;

    try {
      const txId = await walletInterface.executeContractFunction(
        ContractId.fromString(contractId),
        "withdrawDrill",
        params,
        gasLimit,
      );
      console.log("Withdraw transaction submitted:", txId?.toString());
      setSuccess(true);
    } catch (err) {
      console.error("Withdraw call failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async () => {
    if (!walletInterface || !depositAmount) return;

    const amount = Number(depositAmount);
    if (!Number.isInteger(amount) || amount <= 0) {
      console.error("Invalid deposit amount");
      return;
    }
    if (amount > allowance) {
      console.error("Amount exceeds allowance");
      return;
    }
    if (amount > userTdrillBalance) {
      console.error("Amount exceeds your wallet balance");
      return;
    }

    setLoading(true);

    // --- THIS IS THE FIX ---
    // Create the *real* parameters from the SDK
    const hapiParams = new ContractFunctionParameters();
    hapiParams.addUint64(amount);

    // Create a "mock" builder object that has the buildHAPIParams() method
    // that walletInterface expects. We use 'as' to force TypeScript to accept it.
    const params = {
      buildHAPIParams: () => hapiParams,
    } as ContractFunctionParameterBuilder;
    // --- END FIX ---

    const gasLimit = 100_000; // Deposit is more complex, add more gas

    try {
      const txId = await walletInterface.executeContractFunction(
        ContractId.fromString(contractId),
        "depositDrillToken",
        params, // Pass our mock builder object
        gasLimit,
      );
      console.log("Deposit transaction submitted:", txId?.toString());
      setDepositSuccess(true);
      setDepositAmount(""); // Clear input on success
      checkStatus(); // Refresh balances
    } catch (err) {
      console.error("Deposit call failed:", err);
    } finally {
      setLoading(false);
    }
  };

  // --- Helper for deposit button disabled state ---
  const getDepositButtonDisabled = () => {
    if (loading) return true;
    if (!depositAmount) return true;
    const amount = Number(depositAmount);
    if (!Number.isInteger(amount) || amount <= 0) return true;
    if (amount > allowance) return true;
    if (amount > userTdrillBalance) return true;
    return false;
  };

  // --- Helper for deposit error message ---
  const getDepositErrorMessage = () => {
    const amount = Number(depositAmount);
    if (depositAmount && (!Number.isInteger(amount) || amount <= 0)) {
      return "Amount must be a positive whole number.";
    }
    if (amount > userTdrillBalance) {
      return `Amount exceeds your balance of ${userTdrillBalance} Tdrill.`;
    }
    if (amount > allowance) {
      return `Amount exceeds your allowance of ${allowance} Tdrill.`;
    }
    return null;
  };

  const depositError = getDepositErrorMessage();

  // --- RENDER ---
  return (
    <Stack alignItems="center" spacing={4} p={4} maxWidth={600} mx="auto">
      {!accountId ? (
        // --- NOT CONNECTED ---
        <>
          <Typography variant="h4" color="white" align="center">
            {isDepositPhase ? "Deposit Tdrill" : "Withdraw Tdrill"}
</Typography>
          <Typography color="white">
  Connect to withdraw your Tdrill.

          </Typography>
        </>
      ) : isDepositPhase ? (
        // --- DEPOSIT PHASE ---
        <>
          <Typography variant="h4" color="white" align="center">
            Deposit Tdrill
          </Typography>
          {loading ? (
            <Typography color="white">Checking status...</Typography>
          ) : depositSuccess ? (
            <>
              <Typography color="white">Deposit successful!</Typography>
              <Button
                variant="contained"
                onClick={() => setDepositSuccess(false)}
              >
                Back
              </Button>
            </>
          ) : userTdrillBalance === 0 ? (
            <Typography color="error">
              You have no Tdrill ({tdrillTokenId}) in your wallet to deposit.
            </Typography>
          ) : !isUsdcAssociated ? (
            <Typography color="error">
              You must be associated with USDC ({usdcTokenId}) to deposit.
            </Typography>
          ) : allowance === 0 ? (
            <Typography color="error">
              You have not yet given the contract ({contractId}) an allowance to
              spend your Tdrill.
            </Typography>
          ) : (
            // --- DEPOSIT FORM ---
            <>
              <Typography color="white">
                Your Tdrill Allowance: {allowance.toLocaleString()}
                <br />
                Your Tdrill Balance: {userTdrillBalance.toLocaleString()}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                label={`Amount of Tdrill (${tdrillTokenId}) to Deposit`}
                type="number"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                InputLabelProps={{
                  style: { color: "#fff" },
                }}
                InputProps={{
                  style: { color: "#fff", borderColor: "#fff" },
                }}
                error={!!depositError}
                helperText={
                  <span style={{ color: "#f44336" }}>{depositError}</span>
                }
              />
              <Button
                variant="contained"
                onClick={handleDeposit}
                disabled={getDepositButtonDisabled()}
                fullWidth
              >
                Deposit Tdrill
              </Button>
            </>
          )}
        </>
      ) : (
        // --- WITHDRAW PHASE ---
        <>
          <Typography variant="h4" color="white" align="center">
            Withdraw Tdrill
          </Typography>
          {loading ? (
            <Typography color="white">Checking status...</Typography>
          ) : success ? (
            <>
              <Typography color="white">
                Success! Check your wallet to see your Tdrill is back!
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setSuccess(false);
                  checkStatus();
                }}
              >
                Back to dApp
              </Button>
            </>
          ) : !isTdrillAssociated ? (
            <Typography color="error">
              You must be associated with Tdrill ({tdrillTokenId}) to use the
              withdraw feature.
            </Typography>
          ) : userDepositedBalance === BigInt(0) ? (
            <Typography color="error">
              You have no Tdrill balance in the contract to withdraw.
            </Typography>
          ) : !contractHasTdrill ? (
            <Typography color="error">
              Contract has no Tdrill to withdraw.
            </Typography>
          ) : (
            // --- WITHDRAW BUTTON ---
            <>
              <Button variant="contained" onClick={handleWithdraw}>
                Withdraw Your {userDepositedBalance.toString()} Tdrill
              </Button>
              <Typography
                color="white"
                textAlign="center"
                mt={4}
              >
                By calling this contract ({contractId}) you should
                receive all your Tdrill back.
              </Typography>
            </>
          )}
        </>
      )}
<Typography color="white">
        Need help? See the{" "}
        <a
          href="https://tozm.io/?page=drillinstructions"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#90caf9" }}
        >
          detailed instructions
        </a>
        .
        <br />
        <br />
        By using this dApp you signify your agreement to our{" "}
        <a
          href="https://tozm.io/?page=terms"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#90caf9" }}
        >
          Terms
        </a>
        .
      </Typography>
    </Stack>
  );
}

